<template>
  <stepper-page-template
      v-model="step"
      :steps="steps">
    <template #toolbar-items>
      <v-toolbar-title class="with-divider">
        {{ title }}
      </v-toolbar-title>
    </template>

    <trade-contract-info
        :trade-contract="tradeContract"
        :companion="tradeContract.isPublishMode ? null : tradeContract.companions"
        @changeTradeContract="changeTradeContract"
        :readonly="!editable">
    </trade-contract-info>

    <router-view
        :trade-contracts="[tradeContract]"
        :trade-contract-index="1"
        :completable="completable"
        :errors="errors"
        :editable="editable"
        :steps="steps"
        :step="step"
        @save="save()"
        @next-step="step++"
        @prev-step="step--">
    </router-view>
  </stepper-page-template>
</template>

<script>
import TradeContractInfo from "@/components/organisms/TradeContractInfo.vue";
import IoablePageMixin from "@/router/IoablePageMixin";
import {mapActions, mapGetters, mapMutations, mapState} from "vuex";
import StepperPageTemplate from "@/components/templates/StepperPageTemplate.vue";
import {safeMeta} from "@/router/Meta";
import TradeContractUtils from "@/components/pages/contract/TradeContract/TradeContractUtils";
import {
  createTradeContractFormatView,
  emptyTradeContractFormatView
} from "@/components/pages/contract/TradeContractFormat/TradeContractFormatView";

export default {
  name: "DraftContract",
  components: {StepperPageTemplate, TradeContractInfo},
  mixins: [IoablePageMixin.extend({
    beforeRouteUpdate(to, from, next) {
      next(); // 子ページの切り替わりで再読込しない
    },
  }), TradeContractUtils],
  props: {
    tradeContractFormatId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      step: 0,
      loading: true,
      tradeContract: emptyTradeContractFormatView(),
    };
  },
  created() {
    this.$watch(
        () => this.tradeContract.dirty,
        dirty => this.dirty = dirty
    )
  },
  computed: {
    ...mapGetters({
      account: 'hcs/account',
      hcsClient: 'hcs/client',
    }),
    steps() {
      const tradeContract = this.tradeContract;
      const errors = tradeContract.errors();
      const routeBase = this.$route.name.replace(/^(\/[^/]+\/[^/]+).*$/, '$1');
      return [{
        id: 'dataProfile',
        label: '発行側データ',
        name: `${routeBase}/dataProfile`,
        complete: !errors.containsCategory('dataProfile'),
        editable: true,
      }, {
        id: 'serviceProfile',
        label: '発行側サービス',
        name: `${routeBase}/serviceProfile`,
        complete: !errors.containsCategory('serviceProfile'),
        editable: true,
      }, {
        id: 'companionServiceProfile',
        label: '受領側サービス',
        name: `${routeBase}/companionServiceProfile`,
        complete: !errors.containsCategory('companionServiceProfile'),
        editable: true,
      }, {
        id: 'contractProfile',
        label: '契約内容',
        name: `${routeBase}/contractProfile`,
        complete: !errors.containsCategory('contractProfile'),
        editable: true,
      }, {
        id: 'groups',
        label: 'グループ',
        name: `${routeBase}/groups`,
        complete: !errors.containsCategory('formatGroup'),
        editable: true,
      }, {
        id: 'complete',
        label: '保存',
        name: `${routeBase}/complete`,
        complete: !errors.containsCategory('complete'),
        editable: false,
      }];
    },
    /** 編集可能かどうか */
    editable() {
      return this.$route.name.startsWith('/edit/');
    },
    /** 完了可能かどうか */
    completable() {
      for(const step of this.steps) {
        if(!step.complete) {
          return false;
        }
      }
      return true;
    },
    /** 送信設定ステップに表示するエラー */
    errors() {
      const errors = [];
      for(const step of this.steps) {
        if(!step.complete && step.id !== 'complete') {
          errors.push({
            level: 'warn',
            message: [
              this.$createElement('router-link', {
                props: {
                  to: {name: step.name}
                }
              }, [`${step.label}ステップ`]),
              'が未完了です。'
            ]
          });
        }
      }
      return errors;
    },
    /** タイトル */
    title() {
      return safeMeta(this.$route).label;
    },
    /** 戻るボタンの戻り先 */
    backButtonTo() {
      return this.backToLocation(this.tradeContract);
    },
  },
  methods: {
    ...mapMutations({
      showSnackbar: 'showSnackbar',
    }),
    ...mapActions({
      loadContractProfiles: 'hcs/contractProfile/findList',
    }),
    async load() {
      const {data: tradeContract} = await this.hcsClient.get(`/trade_contract_formats/${this.tradeContractFormatId}`);
      // TODO サーバーから以下のパラメーターが付いて帰ってくる様になったら消して良い
      tradeContract.producerServiceComponentModelId = tradeContract.producerServiceComponentModelId ?? null;
      tradeContract.consumerServiceComponentModelId = tradeContract.consumerServiceComponentModelId ?? null;
      tradeContract.producerProcessComponentModelId = tradeContract.producerProcessComponentModelId ?? null;
      tradeContract.consumerProcessComponentModelId = tradeContract.consumerProcessComponentModelId ?? null;
      this.tradeContract = createTradeContractFormatView(tradeContract, this.$store);
      this.tradeContract.loadContractProfiles();
      this.loading = false;
    },
    async save() {
      const tradeContract = this.tradeContract;
      this.showSnackbar({
        message: `取引契約フォーマットの保存中...`,
        timeout: false,
        loading: true
      });

      const loading = this.$loading({
        lock: true,
        spinner: 'el-icon-loading',
      });

      try {
        // 保存
        await this.hcsClient.patch(`/trade_contract_formats/${tradeContract.id}`, tradeContract.buildPatchData());

        this.showSnackbar({
          message: `取引契約フォーマット ${tradeContract.name} を保存しました`,
        });

        // 取引契約フォーマット一覧画面に遷移
        this.dirty = false;
        await this.$router.push('/edit/tradeFormat/list/format');
      } catch (e) {
        console.error(e);
        this.showSnackbar({
          color: 'failed',
          message: `取引契約フォーマット ${tradeContract.name} の保存に失敗しました`,
        });
      } finally {
        loading.close();
      }
    },
    changeTradeContract(tradeContract) {
      this.tradeContract.name = tradeContract.name;
      this.tradeContract.description = tradeContract.description;
    },
  },
  watch: {
    '$route': {
      immediate: true,
      handler(route) {
        this.step = this.steps.findIndex(step => step.name === route.name);
      }
    },
    step: {
      immediate: true,
      handler(step, oldStep) {
        if(step === oldStep) {
          return;
        }
        const query = this.$route.query
        if(step === -1) {
          const name = this.steps[0].name;
          this.$router.replace({name, query}).catch(() => {}); // eslint-disable-line @typescript-eslint/no-empty-function
        } else {
          const name = this.steps[step].name;
          this.$router.push({name, query}).catch(() => {}); // eslint-disable-line @typescript-eslint/no-empty-function
        }
      }
    }
  }
}
</script>

<style scoped>

</style>
